'use client';

import { useEffect } from 'react';

/** Wires a global error handler so unhandled exceptions and promise
 * rejections leave a structured trace in the console with the URL +
 * timestamp + reset-friendly tag, even before React's own error
 * overlays render. Useful for tracking down rare prod errors that
 * only happen on certain pages — pair with productionBrowserSourceMaps
 * in next.config to get readable stack frames. */
export function ClientErrorLogger() {
  useEffect(() => {
    const onError = (event: ErrorEvent) => {
      // eslint-disable-next-line no-console
      console.error(
        '[client-error]',
        {
          url: location.href,
          message: event.message,
          source: event.filename,
          line: event.lineno,
          col: event.colno,
          time: new Date().toISOString(),
        },
        event.error,
        event.error?.stack,
      );
    };

    const onRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason as unknown;
      // eslint-disable-next-line no-console
      console.error(
        '[client-rejection]',
        {
          url: location.href,
          time: new Date().toISOString(),
        },
        reason,
        reason instanceof Error ? reason.stack : undefined,
      );
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  return null;
}
